import { ReactNode } from "react";

import { Radio, RadioGroup } from "@blueprintjs/core";
import { addMinutes, isBefore, parseISO } from "date-fns";
import Select from "react-select";

import { Box, Flex } from "@/components/layout/flexbox";
import { ComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { CalendarIcon } from "@/components/pages_logged_in/program/components/set-components/configure-inputs/CalendarIcon";
import { useDateTimeConstraints } from "@/components/pages_logged_in/program/components/set-components/configure-inputs/hooks";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { ProgramStatus } from "@/graphql";

/**
 * Duration options for group meetings
 * Generated by Copilot
 */
const DURATION_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "60 minutes" },
  { value: 90, label: "90 minutes" },
  { value: 120, label: "120 minutes" },
];

/**
 * Props for the DateInputWithIcon component
 * Generated by Copilot
 */
interface DateInputWithIconProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  onChangeTimezone: (timezone: string) => void;
  timezone?: string | null;
  min: Date;
  max: Date;
  disabled?: boolean;
}

/**
 * A date input with a calendar icon
 * Generated by Copilot
 */
export function DateInputWithIcon({
  value,
  onChange,
  onChangeTimezone,
  timezone,
  min,
  max,
  disabled,
}: DateInputWithIconProps) {
  return (
    <Box css={{ position: "relative" }}>
      <CalendarIcon />
      <DateTimeInputV2
        value={value}
        onChange={onChange}
        onChangeTimezone={onChangeTimezone}
        timezone={timezone}
        minDateTime={min}
        maxDateTime={max}
        disabled={disabled}
      />
    </Box>
  );
}

/**
 * Props for the DateTimeFormInput component
 * Generated by Copilot
 */
interface DateTimeFormInputProps {
  componentMetadata: ComponentMetadata;
  launchDate?: string | null;
  setLaunchDate: (date: string | null) => void;
  setTimezone: (timezone: string) => void;
  timezone?: string;
  dueDate?: string | null;
  setDueDate: (date: string | null) => void;
  endDate?: string | null;
  setEndDate: (date: string | null) => void;
  hasChangeStartDate: boolean;
  hasChangeDueDate: boolean;
  hasChangeEndDate: boolean;
  errorType: string;
  message: string;
  valid: boolean;
  onParticipantPrelaunchVisibilityChange: (visible: boolean) => void;
  onVisibleUpcomingQuestionsChange: (visible: boolean) => void;
  participantPrelaunchVisibility: boolean;
  visibleUpcomingQuestions: boolean;
  duration: number;
  setDuration: (duration: number) => void;
  autoLaunchWithProgram: boolean;
  setAutoLaunchWithProgram: (autoLaunchWithProgram: boolean) => void;
  programLaunchDate?: string | null;
  programStatus: ProgramStatus;
}

/**
 * Form input for date and time configuration
 * Generated by Copilot
 */
export function DateTimeFormInput({
  componentMetadata,
  launchDate,
  setLaunchDate,
  setTimezone,
  timezone,
  dueDate,
  setDueDate,
  endDate,
  setEndDate,
  hasChangeStartDate,
  hasChangeDueDate,
  hasChangeEndDate,
  errorType,
  message,
  valid,
  onParticipantPrelaunchVisibilityChange,
  onVisibleUpcomingQuestionsChange,
  participantPrelaunchVisibility,
  visibleUpcomingQuestions,
  duration,
  setDuration,
  autoLaunchWithProgram,
  setAutoLaunchWithProgram,
  programLaunchDate,
  programStatus,
}: DateTimeFormInputProps) {
  const {
    minLaunchDate,
    minDueDate,
    minEndDate,
    maxLaunchDate,
    maxDueDate,
    maxEndDate,
  } = useDateTimeConstraints({
    launchDate,
    dueDate,
    endDate,
    hasChangeStartDate,
    hasChangeDueDate,
    hasChangeEndDate,
  });
  const status = componentMetadata.status;
  const isProgramNotLaunched = programLaunchDate
    ? isBefore(new Date(), parseISO(programLaunchDate))
    : false;

  const isTimeEditable =
    ["Upcoming", "InDesign", "Soon"].includes(status) ||
    [ProgramStatus.InDesign, ProgramStatus.LaunchReady].includes(programStatus);

  /**
   * Renders an error message if the condition is met
   * Generated by Copilot
   */
  const renderError = (
    errorType: string,
    targetType: string,
    message: string,
  ): ReactNode | null => {
    if (!valid && errorType === targetType) {
      return (
        <Box color={"#c23030"} fontSize={14} mt={10}>
          {message}
        </Box>
      );
    }

    return null;
  };

  const errorEndDate = renderError(errorType, "targetEndDate", message);
  const errorLaunchDate = renderError(errorType, "targetStartDate", message);
  const errorDueDate = renderError(errorType, "targetDueDate", message);

  const launchDateLabel = componentMetadata.isGroupMeeting
    ? "Schedule"
    : "Launch Date";

  /**
   * Handles launch date changes and updates the end date based on duration
   * Generated by Copilot
   */
  const handleLaunchDateChange = (value?: string | null) => {
    setLaunchDate(value ?? null);
    if (duration && componentMetadata.isGroupMeeting && value) {
      const launchDate = parseISO(value);
      const newEndDate = addMinutes(launchDate, duration);
      setEndDate(newEndDate.toISOString());
    }
  };

  /**
   * Handles duration changes and updates the end date
   * Generated by Copilot
   */
  const handleDurationChange = (value: number) => {
    setDuration(value);
    if (launchDate) {
      const launchDateDate = parseISO(launchDate);
      const newEndDate = addMinutes(launchDateDate, value);
      setEndDate(newEndDate.toISOString());
    }
  };

  return (
    <>
      {/* Launch date */}
      <StyledFormGroup
        label={launchDateLabel}
        subLabel={componentMetadata.launchDateDesc}
      >
        {isProgramNotLaunched && !componentMetadata.isGroupMeeting && (
          <RadioGroup
            inline
            onChange={(e) => {
              if (e.currentTarget.value === "true") {
                setAutoLaunchWithProgram(true);
                handleLaunchDateChange(programLaunchDate);
              } else {
                setAutoLaunchWithProgram(false);
              }
            }}
            selectedValue={autoLaunchWithProgram ? "true" : "false"}
          >
            <Radio label="Specific date and time" value="false" />
            <Radio label="At program launch" value="true" />
          </RadioGroup>
        )}
        <DateInputWithIcon
          value={launchDate}
          onChange={handleLaunchDateChange}
          onChangeTimezone={setTimezone}
          timezone={timezone}
          min={minLaunchDate}
          max={maxLaunchDate}
          disabled={!isTimeEditable || autoLaunchWithProgram}
        />
        {/* Visible to participants pre-launch */}
        {componentMetadata.isAnnouncement && (
          <Flex my="10px">
            <StandaloneCheckbox
              onChange={() =>
                onParticipantPrelaunchVisibilityChange(
                  !participantPrelaunchVisibility,
                )
              }
              checked={participantPrelaunchVisibility}
            />
            <Clickable
              onClick={() =>
                onParticipantPrelaunchVisibilityChange(
                  !participantPrelaunchVisibility,
                )
              }
            >
              <Box style={{ fontSize: 16 }} ml={2}>
                Visible to participants pre-launch
              </Box>
            </Clickable>
          </Flex>
        )}
        {/* Action item visible to participants before launch */}
        {componentMetadata.isActionItems && (
          <Flex my="10px">
            <StandaloneCheckbox
              onChange={() =>
                onVisibleUpcomingQuestionsChange(!visibleUpcomingQuestions)
              }
              checked={visibleUpcomingQuestions}
            />
            <Clickable
              onClick={() =>
                onVisibleUpcomingQuestionsChange(!visibleUpcomingQuestions)
              }
            >
              <Box style={{ fontSize: 16 }} ml={2}>
                Action item visible to participants before launch
              </Box>
            </Clickable>
          </Flex>
        )}
        {/* Error message for launch date */}
        {errorLaunchDate}
      </StyledFormGroup>

      {/* Due date: only show for non-group meeting components */}
      {componentMetadata.hasParticipantGuidanceAndDueDate &&
        !componentMetadata.isGroupMeeting && (
          <StyledFormGroup
            label="Due Date"
            subLabel={componentMetadata.dueDateDesc}
          >
            <DateInputWithIcon
              value={dueDate}
              onChange={setDueDate}
              onChangeTimezone={setTimezone}
              timezone={timezone}
              min={minDueDate}
              max={maxDueDate}
            />
            {errorDueDate}
          </StyledFormGroup>
        )}

      {/* Close date: only show for non-group meeting components */}
      {!componentMetadata.isGroupMeeting && (
        <StyledFormGroup
          label="Close Date"
          subLabel={componentMetadata.closeDateDesc}
        >
          <DateInputWithIcon
            value={endDate}
            onChange={setEndDate}
            onChangeTimezone={setTimezone}
            timezone={timezone}
            min={minEndDate}
            max={maxEndDate}
          />
          {errorEndDate}
        </StyledFormGroup>
      )}

      {/* Duration: only show for group meeting components */}
      {componentMetadata.isGroupMeeting && (
        <StyledFormGroup label="Duration">
          <Box css={{ position: "relative" }}>
            <Select
              value={DURATION_OPTIONS.find((o) => o.value === duration)}
              onChange={(option) => handleDurationChange(option?.value ?? 0)}
              options={DURATION_OPTIONS}
              isSearchable={false}
              styles={{
                control: (styles) => ({
                  ...styles,
                }),
              }}
            />
          </Box>
          {errorEndDate}
        </StyledFormGroup>
      )}
    </>
  );
}
